import { getLogger } from '@sas/nova-core/logging';

import { GetEntity, GetLibraryEntity, GetTableHistory } from '../client/catalog/rest';
import { GetContexts } from '../client/compute/rest';
import { DataSourcesProviderCas, DataSourcesProviderCompute } from '../client/data/api';
import { ExtractProviderAndServer, ResolveTableURI } from '../client/data/resolver_fast';
import { CheckAuthorization } from '../client/data/rest';
import { Entity } from '../representations/catalog';
import { User } from '../representations/common';
import { Library, Table } from '../representations/data';
import { TrimExtension } from '../util/strings';
import { GetTableDetailsOptions } from './api';
import { GetUserFromCache } from './identities';

// eslint-disable-next-line @sas/i18n/jsx-to-localize
const logger = getLogger('DataSelection');

// 189
export const GetTableDetailsByEntity = async (
  entityURI: string,
  options?: GetTableDetailsOptions
): Promise<[Table | undefined, Error?]> => {
  // get catalogEntity from catalog service
  const [catalogEntity, err] = await GetEntity(entityURI);
  if (err || catalogEntity === undefined) {
    // pi18n.LogGenericError(ctx, d.logger, err, util.FunctionName())
    // var errorMessage = i18n.GetMessage(ctx, i18n.L10nGetEntityError, si18n.P{"errorMessage": err.Error()})
    // return nil, util.ErrorFromPossiblyCommonsError(ctx, err, errorMessage)
    return [undefined, err];
  }

  const entityMap = await getTableDetailsByEntity([catalogEntity], options);

  // there should only be one entry in the map
  const entityOrError = entityMap.get(catalogEntity.id);
  if (!entityOrError) {
    logger.debug('GetTableDetailsByEntity - somehow no entity or error was returned.');
    return [undefined, undefined];
  }
  if (entityOrError instanceof Error) {
    return [undefined, entityOrError];
  }
  return [entityOrError, undefined];
};

// 216
// getTableDetailsByEntity will return a map of string -> interface{} where the string key is the id of the entity and the value is a *representations.Table or an error
const getTableDetailsByEntity = async (
  catalogEntities: Entity[],
  options?: GetTableDetailsOptions
): Promise<Map<string, any>> => {
  let authCheck = true;
  let extendedDetails = true;
  let resolveComputeContextName = true;
  if (options !== undefined) {
    authCheck = options.authCheck;
    extendedDetails = options.extendedDetails;
    resolveComputeContextName = options.resolveComputeContextName;
  }
  const ret: Map<string, any> = new Map();
  const entityCount = catalogEntities.length;
  const warningLevel = 5;
  if (authCheck) {
    if (entityCount > warningLevel) {
      logger.debug(
        'NOTE: Checking authorizations for multiple catalog entities is not currently parallelized, which may result in poor performance'
      );
    }
    for (const catalogEntity of catalogEntities) {
      logger.debug(`Checking for user authorization on ${catalogEntity.resourceID}`);
      // check if the user is authorized for the entity's table (if it is a cas table)
      const err = await CheckAuthorization(catalogEntity.resourceID, catalogEntity.name);
      if (err) {
        logger.debug(
          `There was an error while checking for authorization for ${catalogEntity.resourceID}: ${err}`
        );
        ret.set(catalogEntity.id, err);
        continue;
      }
      logger.debug(`Authorization check succeeded for ${catalogEntity.resourceID}`);
    }
  }

  const resolvedLibraries: Map<string, Library> = new Map();
  let contextToNameMap: Map<string, string> | undefined;
  const userCacheMap: Map<string, User> = new Map();

  for (const catalogEntity of catalogEntities) {
    // if it failed the auth check, there will already be an entry in the ret map, so just move onto the next one.
    if (ret.get(catalogEntity.id)) {
      continue;
    }
    const response: Table = {
      id: catalogEntity.id,
      uri: catalogEntity.resourceID,
      name: TrimExtension(catalogEntity.name),
      encoding: catalogEntity.attributes.encoding || '', // AttributeToString
      dateAnalyzed: catalogEntity.attributes.analysisTimeStamp || '', // AttributeToString
      dateCreated: catalogEntity.attributes.dateCreated || '', // AttributeToString
      dateModified: catalogEntity.attributes.dateModified || '', // AttributeToString
      description: catalogEntity.description,
      label: catalogEntity.label,
      dataLocale: catalogEntity.attributes.dataLocale || '', // AttributeToString
      attributes: catalogEntity.attributes,
    };

    const createdBy = catalogEntity.attributes.creator || ''; // AttributeToString
    const modifiedBy = catalogEntity.attributes.editor || ''; // AttributeToString
    const responsibleParty = catalogEntity.attributes.responsibleParty || ''; // AttributeToString

    // lookup users
    response.responsibleParty = await GetUserFromCache(responsibleParty, userCacheMap);
    response.createdBy = await GetUserFromCache(createdBy, userCacheMap);
    response.modifiedBy = await GetUserFromCache(modifiedBy, userCacheMap);
    //  fix these before we assign the attributes map, below
    if (response.createdBy !== undefined) {
      catalogEntity.attributes.creator = response.createdBy.name;
    }
    if (response.modifiedBy !== undefined) {
      catalogEntity.attributes.editor = response.modifiedBy.name;
    }

    if (catalogEntity.type === 'casTable') {
      response.sourceTableName = catalogEntity.attributes.sourceName || ''; // AttributeToString
    }

    // get data store info
    const [tableSummary, err] = ResolveTableURI(catalogEntity.resourceID);
    let provider, serverID, library;
    if (err || !tableSummary) {
      logger.debug(
        `There was an error while extracting the data provider and server from ${catalogEntity.resourceID}. Default to using the ${DataSourcesProviderCas} provider. The error was ${err}`
      );
    } else {
      provider = tableSummary.providerId;
      library = tableSummary.dataSourceId;
      if (tableSummary.attributes) {
        const server = tableSummary.attributes.serverId;
        if (!server) {
          logger.debug(
            `The serverId attribute was missing on the table summary.  Attempting to extract the serverID from ${catalogEntity.resourceID}`
          );
          const { provider: p, server: s, err } = ExtractProviderAndServer(
            catalogEntity.resourceID
          );
          if (err) {
            // if this happens, the resource URI is probably bad. The rest of the info will get filled in as it can below.
            logger.debug(`Attempting to extract the serverID from the table URI failed: ${err}`);
          } else {
            provider = p;
            serverID = s;
          }
        } else {
          serverID = server;
        }
      }
      response.server = serverID;
    }
    const dataStoreLookupKey = `${provider}_${serverID}_${library}`;
    let dataStore = resolvedLibraries.get(dataStoreLookupKey);
    if (!dataStore) {
      logger.debug(`Looking up library entity for table ${catalogEntity.resourceID}`);
      // only look up the library entity once per library and tuck those away so we can avoid making a bunch of calls to catalog
      // there is an opportunity for performance improvement here for when entityCount > 1 by providing a bulk call for getting
      // multiple library entities
      const [ds, err] = await GetLibraryEntity(catalogEntity.id, provider);
      if (err) {
        logger.debug(
          `Ignoring error returned while getting library information for catalog entity ${catalogEntity.id}`
        );
      } else if (ds) {
        logger.debug(`Found library entity with id ${ds.id} for table ${catalogEntity.resourceID}`);
        dataStore = ds;
        resolvedLibraries.set(dataStoreLookupKey, dataStore);
      }
    } else {
      logger.debug(
        `Table ${catalogEntity.resourceID} shares a library entity with a previous table`
      );
    }

    if (dataStore) {
      // engine attribute depends on data store definition
      // dataStore.Entity.Type could be sasLibrary or casLibrary. dataStore.Type is coming from the Attributes, where type might be "PATH"
      if (dataStore.entity.type === 'sasLibrary') {
        response.engine = dataStore.engine;
      } else if (dataStore.entity.type === 'casLibrary') {
        response.engine = dataStore.type; // from Attributes.Type
        response.path = dataStore.path;
      }

      // region
      response.region = dataStore.region;

      // library
      response.library = dataStore.name;

      // parse data store uri only if we couldn't get it from the catalogEntity's resourceID for some reason
      if (!serverID) {
        let err2;
        ({ provider, server: response.server, err: err2 } = ExtractProviderAndServer(
          dataStore.resourceID
        ));
        if (err2) {
          logger.debug(
            `There was an error extracting the provider and server from ${dataStore.resourceID}.  The error was ${err}`
          );
        }
      }
      if (response.server && response.server.indexOf(`/${response.library}`) > 0) {
        logger.debug(`Extracting the server name from the path ${response.server}`);
        response.server = response.server.substring(
          0,
          response.server.indexOf(`/${response.library}`)
        );
      }
    } else {
      if (tableSummary) {
        response.library = library;
        response.server = serverID;
      }
    }

    if (provider === DataSourcesProviderCompute) {
      if (resolveComputeContextName) {
        if (!contextToNameMap) {
          logger.debug('Retrieving the list of compute contexts from the compute client');
          // lookup compute context name for context id and set as server if found
          const [ctnm, err] = await GetContexts();
          if (err) {
            logger.debug(
              `There was an error while querying the compute context list: ${err}.  The compute context id ${response.server} will be returned instead of the pretty name.`
            );
          } else {
            contextToNameMap = ctnm!;
            logger.debug(`Found ${contextToNameMap.size} compute contexts`);
          }
        } else {
          logger.debug(
            `Looking up compute context ${response.server} from previously retrieved map`
          );
        }
        if (response.server && contextToNameMap && contextToNameMap.get(response.server)) {
          response.server = contextToNameMap.get(response.server)!;
        }
      }
    }
    response.columnCount = catalogEntity.attributes.columnCount; // AttributeToUInt64Ptr
    response.rowCount = catalogEntity.attributes.rowCount; // AttributeToUInt64Ptr
    response.dataSizeBytes = catalogEntity.attributes.dataSize; // AttributeToUInt64Ptr
    if (
      response.dataSizeBytes !== undefined &&
      response.dataSizeBytes === 0 &&
      response.rowCount !== undefined &&
      response.rowCount > 0
    ) {
      response.dataSizeBytes = undefined;
    }

    response.stats = {
      completenessPercent: catalogEntity.attributes.completenessPercent, // AttributeToDoublePtr
      informationPrivacy: catalogEntity.attributes.informationPrivacy || '', // AttributeToString
      minTimestamp: catalogEntity.attributes.minTimeStamp || '', // AttributeToString
      maxTimestamp: catalogEntity.attributes.maxTimeStamp || '', // AttributeToString
    };
    if (extendedDetails) {
      if (entityCount > warningLevel) {
        logger.debug(
          'NOTE: Checking extended for multiple catalog entities is not currently parallelized, which may result in poor performance'
        );
      }
      response.extendedTableAttributes = {
        entity: catalogEntity,
      };
      const [tableHistory, err] = await GetTableHistory(catalogEntity.id, provider);
      if (err) {
        logger.debug(
          `Unable to retrieve history for table ${catalogEntity.resourceID}.  The error was ${err}`
        );
      } else {
        response.extendedTableAttributes.history = tableHistory!;
      }
    }

    ret.set(catalogEntity.id, response);
  }

  return ret;
};
